import { useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router'
import { ApiError } from '../../lib/api.js'
import { useAuth } from '../../lib/auth.js'
import { Button, Input } from '../../ui/index.js'
import { usePageTitle } from '../../lib/page-title.js'
import { AuthLayout } from './AuthLayout.js'
import { FieldError, FormError } from './FormError.js'


export function ResetPassword() {
  usePageTitle('Reset password')
  const { api } = useAuth()
  const [params] = useSearchParams()
  const token = params.get('token')
  const [password, setPassword] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [formError, setFormError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)

  async function submit(event: FormEvent) {
    event.preventDefault()
    setFormError(null)

    const next: Record<string, string> = {}
    if (password.length === 0) next['password'] = 'Enter a new password'
    setErrors(next)
    if (Object.keys(next).length > 0 || token === null) return

    setBusy(true)
    try {
      await api.post('/api/auth/reset-password', { token, password })
      setDone(true)
    } catch (error) {
      if (error instanceof ApiError && error.isValidation && error.details !== undefined) {
        setErrors(error.details)
      } else if (error instanceof ApiError) {
        setFormError(error.message)
      } else {
        setFormError('Something went wrong. Try again.')
      }
    } finally {
      setBusy(false)
    }
  }

  if (token === null) {
    return (
      <AuthLayout title="Reset password" subtitle="Set a new password.">
        <p>This link is incomplete — ask your manager to send the reset link again.</p>
        <div style={{ marginTop: 'var(--o-space-5)' }}>
          <Link to="/login">Sign in</Link>
        </div>
      </AuthLayout>
    )
  }

  if (done) {
    return (
      <AuthLayout title="Password updated" subtitle="You can sign in again now.">
        <p>
          Your password has been reset. <Link to="/login">Sign in</Link>
        </p>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout title="Reset password" subtitle="Set a new password.">
      <form onSubmit={submit} noValidate>
        <div className="field" style={{ marginBottom: 'var(--o-space-5)' }}>
          <label className="field-label" htmlFor="password">
            New password
          </label>
          <Input
            id="password"
            name="password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={setPassword}
          />
          <FieldError message={errors['password']} />
        </div>
        <FormError message={formError} />
        <Button type="submit" kind="primary" disabled={busy}>
          Reset
        </Button>
      </form>
    </AuthLayout>
  )
}
